import AuthService from '../../services/auth.service'
import Routes from '../../constants/routes'
import { handleErrors } from '../../utils/request.helper'
import { updateConfig } from '../../configs/config'
import SessionStorageService from '../../services/session-storage.service'
import { logoutUser } from '../middlewares/logoutMiddleware'
import { authPersistActions } from '../slices/auth.persist.slice'

/***************************************** INITIAL STATE ********************************************/
export const INITIAL_STATE = {
    user: null,
    email: '',
    password: '',
    message: '',
    isWaitingForLogin: false,
    isWaitingForGetCurrentUser: true,
    resetPasswordSucceed: false,
}

/***************************************** ACTION TYPES *********************************************/
const reducerName = 'AUTH_'
const actionTypes = {
    SET_USER: `${reducerName}SET_USER`,
    SET_EMAIL: `${reducerName}SET_EMAIL`,
    SET_PASSWORD: `${reducerName}SET_PASSWORD`,
    SET_MESSAGE: `${reducerName}SET_MESSAGE`,
    SET_IS_WAITING_FOR_LOGIN: `${reducerName}SET_IS_WAITING_FOR_LOGIN`,
    SET_IS_WAITING_FOR_GET_CURRENT_USER: `${reducerName}SET_IS_WAITING_FOR_GET_CURRENT_USER`,
    SET_RESET_PASSWORD_SUCCEED: `${reducerName}SET_RESET_PASSWORD_SUCCEED`,
    SET_OTP_CONFIGURED: `${reducerName}SET_OTP_CONFIGURED`,
    SET_IMPERSONATED: `${reducerName}SET_IMPERSONATED`,
}

/******************************************** ACTIONS ***********************************************/
export const actions = {
    setUser: value => ({
        type: actionTypes.SET_USER,
        payload: { value },
    }),
    setEmail: value => ({
        type: actionTypes.SET_EMAIL,
        payload: { value },
    }),
    setOtpConfigured: value => ({
        type: actionTypes.SET_OTP_CONFIGURED,
        payload: { value },
    }),
    setPassword: value => ({
        type: actionTypes.SET_PASSWORD,
        payload: { value },
    }),
    setMessage: value => ({
        type: actionTypes.SET_MESSAGE,
        payload: { value },
    }),
    setIsWaitingForLogin: value => ({
        type: actionTypes.SET_IS_WAITING_FOR_LOGIN,
        payload: { value },
    }),
    setIsWaitingForGetCurrentUser: value => ({
        type: actionTypes.SET_IS_WAITING_FOR_GET_CURRENT_USER,
        payload: { value },
    }),
    setResetPasswordSucceed: value => ({
        type: actionTypes.SET_RESET_PASSWORD_SUCCEED,
        payload: { value },
    }),
    login: navigate => async (dispatch, getState) => {
        dispatch(actions.setIsWaitingForLogin(true))
        await handleErrors(
            async () => {
                await AuthService.login(getState().auth.email, getState().auth.password)
                await dispatch(actions.fetchCurrentUser())

                const wantedURL = SessionStorageService.getWantedURL()
                if (wantedURL) {
                    navigate(wantedURL)
                    SessionStorageService.setWantedURL('')
                } else {
                    navigate(Routes.HOME)
                }
            },
            message => dispatch(actions.setMessage(message)),
        )
        dispatch(actions.setIsWaitingForLogin(false))
    },
    logout: () => async dispatch => {
        return await handleErrors(async () => {
            await AuthService.logout()
            dispatch(logoutUser())
            dispatch(authPersistActions.setImpersonated(false))
            //localStorage.clear() #TODO: clear selectively
        })
    },
    fetchCurrentUser: () => async dispatch => {
        dispatch(actions.setIsWaitingForGetCurrentUser(true))
        const user = await handleErrors(async () => {
            const getCurrentUserResponse = await AuthService.getCurrentUser()
            dispatch(actions.setUser(getCurrentUserResponse.data))
            dispatch(actions.setIsWaitingForGetCurrentUser(false))
            return getCurrentUserResponse.data
        })
        dispatch(actions.setIsWaitingForGetCurrentUser(false))
        return user
    },
    fetchAppConfig: () => async () => {
        const config = await handleErrors(async () => {
            const response = await AuthService.getAppConfig()
            return response.data
        })
        updateConfig(config)
        return config
    },
    resetPassword: (code, newPassword) => async dispatch => {
        return await handleErrors(async () => {
            await AuthService.resetPassword(code, newPassword)
            dispatch(actions.setResetPasswordSucceed(true))
        })
    },
    isSenseUser: () => (dispatch, getState) => getState().auth.user?.email.includes('sense.network'),
}

/******************************************* REDUCER ************************************************/
const authReducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case actionTypes.SET_USER:
            return {
                ...state,
                user: action.payload.value,
            }

        case actionTypes.SET_EMAIL:
            return {
                ...state,
                email: action.payload.value,
            }

        case actionTypes.SET_PASSWORD:
            return {
                ...state,
                password: action.payload.value,
            }

        case actionTypes.SET_OTP_CONFIGURED:
            return {
                ...state,
                user: {
                    ...state.user,
                    otp_configured: action.payload.value,
                },
            }

        case actionTypes.SET_IMPERSONATED:
            return {
                ...state,
                impersonated: action.payload.value,
            }

        case actionTypes.SET_MESSAGE:
            return {
                ...state,
                message: action.payload.value,
            }

        case actionTypes.SET_IS_WAITING_FOR_LOGIN:
            return {
                ...state,
                isWaitingForLogin: action.payload.value,
            }

        case actionTypes.SET_IS_WAITING_FOR_GET_CURRENT_USER:
            return {
                ...state,
                isWaitingForGetCurrentUser: action.payload.value,
            }

        case actionTypes.SET_RESET_PASSWORD_SUCCEED:
            return {
                ...state,
                resetPasswordSucceed: action.payload.value,
            }

        default:
            return state
    }
}

export default authReducer
